function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useApolloClient } from "@apollo/client";
import { NavigationContext, NavigationRouteContext, useNavigation } from "@react-navigation/core";
import * as Sentry from "@sentry/core";
import { createElement, useMemo } from "react";
import { TouchableOpacity } from "react-native";
import { ARTIFACT_HANDLERS } from "./ChatInputArtifact";
import { Button } from "./Button";
import { View } from "./View";
import { APP_SLUG } from "../constants";
import { useChatSideEffect } from "../hooks/useChatSideEffect";
import { QuizSetPreview } from "../screens/QuizSet";
import { CHAT_PREVIEW_BORDER_RADIUS } from "../styles";
var HEIGHT = 125;
var WIDTH = 210;
var PADDING = 0;
var PREVIEW_COMPONENTS = {
    QuizSet: function(param) {
        var width = param.width, height = param.height;
        return function(param) {
            var slug = param.slug;
            return /*#__PURE__*/ _jsx(QuizSetPreview, {
                width: width,
                height: height,
                slug: slug
            });
        };
    }
};
/**
 * @nosync
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-56}
 */ export function registerChatArtifactPreviewComponent(key, elementFn) {
    PREVIEW_COMPONENTS[key] = elementFn;
}
/**
 * For use in a chat conversation. Renders a preview element for an artifact
 * related to the conversation. Upon clicking, navigates with the provided
 * information so the user can interact with the artifact.
 *
 * The preview is wrapped with mock navigation contexts so that previews cannot
 * take over the current navigation state when not actually part of the
 * navigation stack.
 *
 * Artifacts can customize an appropriate preview component via
 * `registerChatArtifactPreviewComponent`. The callback function is invoked
 * with the width/height of the element to be rendered at a 0.5 scale.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-56}
 */ export function ChatArtifactPreview(param) {
    var artifactName = param.artifactName, linkText = param.linkText, testID = param.testID, params = param.params, _height = param._height, _width = param._width, _onSideEffect = param._onSideEffect, _borderColor = param._borderColor;
    var width = _width !== null && _width !== void 0 ? _width : WIDTH;
    var height = _height !== null && _height !== void 0 ? _height : HEIGHT;
    var onSideEffect = useChatSideEffect();
    var apollo = useApolloClient();
    var navigation = useNavigation();
    var navigate = navigation.navigate;
    var routeName = artifactName;
    var previewComponent = useMemo(function() {
        var _PREVIEW_COMPONENTS_routeName;
        var _PREVIEW_COMPONENTS_routeName1;
        return (_PREVIEW_COMPONENTS_routeName1 = (_PREVIEW_COMPONENTS_routeName = PREVIEW_COMPONENTS[routeName]) === null || _PREVIEW_COMPONENTS_routeName === void 0 ? void 0 : _PREVIEW_COMPONENTS_routeName.call(PREVIEW_COMPONENTS, {
            width: width,
            height: height,
            defaultHeight: HEIGHT
        })) !== null && _PREVIEW_COMPONENTS_routeName1 !== void 0 ? _PREVIEW_COMPONENTS_routeName1 : null;
    }, [
        routeName,
        width,
        height
    ]);
    if (APP_SLUG === 'oui-aviva-staff') {
        /*
    We cannot render a component preview from within the CMS because we have a dependency 
    on a Modal wrapper. This wrapper is only provided in the app.
  */ previewComponent = null;
    }
    var shouldTransformPreview = routeName !== 'QuizSet';
    var onPress = function() {
        if (routeName in ARTIFACT_HANDLERS && // these types don't match 100% but if the key exists then we are OK
        ARTIFACT_HANDLERS[routeName]) {
            var _ARTIFACT_HANDLERS_routeName;
            (_ARTIFACT_HANDLERS_routeName = ARTIFACT_HANDLERS[routeName]) === null || _ARTIFACT_HANDLERS_routeName === void 0 ? void 0 : _ARTIFACT_HANDLERS_routeName.call(ARTIFACT_HANDLERS, {
                startArtifactRequest: function() {},
                params: params,
                apollo: apollo,
                navigate: navigate
            }).catch(Sentry.captureException);
        } else {
            (_onSideEffect !== null && _onSideEffect !== void 0 ? _onSideEffect : onSideEffect)({
                kind: 'navigate',
                routeName: routeName,
                params: _object_spread({}, params)
            });
        }
    };
    var spoofedNavigationContext = useMemo(function() {
        return _object_spread_props(_object_spread({}, navigation), {
            // We don't want preview components that happen to be screens to be able
            // to control the current navigation since they aren't actually the
            // focused/active screen
            isFocused: function() {
                return false;
            },
            setOptions: function() {},
            addListener: function() {
                return function() {};
            },
            removeListener: function() {},
            navigate: function() {},
            dispatch: function() {},
            reset: function() {}
        });
    }, [
        navigation
    ]);
    return /*#__PURE__*/ _jsx(TouchableOpacity, {
        onPress: onPress,
        children: /*#__PURE__*/ _jsxs(View, {
            spacing: 10,
            children: [
                /*#__PURE__*/ _jsx(View, {
                    style: {
                        width: width,
                        height: height,
                        borderRadius: CHAT_PREVIEW_BORDER_RADIUS,
                        borderWidth: artifactName === 'HopeKit' ? 0 : 1,
                        borderColor: _borderColor,
                        padding: PADDING
                    },
                    "aria-hidden": true,
                    children: /*#__PURE__*/ _jsx(View, {
                        style: [
                            {
                                flexGrow: 1,
                                overflow: 'hidden'
                            },
                            shouldTransformPreview ? {
                                borderRadius: CHAT_PREVIEW_BORDER_RADIUS * 2,
                                height: height * 2 - PADDING * 2 - 2,
                                transform: [
                                    {
                                        scale: 0.5
                                    },
                                    {
                                        translateY: -height
                                    },
                                    {
                                        translateX: -width
                                    }
                                ],
                                width: width * 2 - PADDING * 2 - 2
                            } : {
                                borderRadius: CHAT_PREVIEW_BORDER_RADIUS,
                                height: height - PADDING - 2,
                                width: width - PADDING - 2
                            }
                        ],
                        pointerEvents: "none",
                        children: previewComponent ? /*#__PURE__*/ _jsx(NavigationContext.Provider, {
                            value: spoofedNavigationContext,
                            children: /*#__PURE__*/ _jsx(NavigationRouteContext.Provider, {
                                value: {
                                    key: 'preview',
                                    name: routeName,
                                    params: params
                                },
                                children: /*#__PURE__*/ createElement(previewComponent, _object_spread({
                                    preview: true
                                }, params))
                            })
                        }) : null
                    })
                }),
                /*#__PURE__*/ _jsx(Button, {
                    testID: testID,
                    onPress: onPress,
                    text: linkText,
                    variant: "text",
                    alignSelf: "center",
                    style: {
                        maxWidth: width
                    }
                })
            ]
        })
    });
}
